// Design Ref: §4.2 LineChart — SVG path-based 누적 수익률
// Plan SC: FR-02 누적 수익률 시계열
import React from 'react';

/**
 * Props:
 *  - data: [{ label: '2026-03', value: 3.2 }, ...]  // value = %
 *  - title?
 *  - height? (default 180)
 *  - color? (default '#f0c040')
 *  - formatValue?: (n) => string
 */
export default function LineChart({
  data = [],
  title,
  height = 180,
  color = '#f0c040',
  formatValue = (n) => `${n >= 0 ? '+' : ''}${n.toFixed(2)}%`,
}) {
  if (data.length < 2) {
    return <EmptyBlock title={title} height={height} msg={data.length === 0 ? '데이터 없음' : '최소 2개월 필요'} />;
  }
  const W = 400;
  const H = height - 40; // label 공간 확보
  const values = data.map(d => Number(d.value) || 0);
  const max = Math.max(...values, 0);
  const min = Math.min(...values, 0);
  const range = max - min || 1;
  const toX = (i) => data.length === 1 ? W / 2 : (i / (data.length - 1)) * W;
  const toY = (v) => H - ((v - min) / range) * H;
  const path = data.map((d, i) => `${i === 0 ? 'M' : 'L'} ${toX(i).toFixed(1)} ${toY(d.value).toFixed(1)}`).join(' ');
  const zeroY = toY(0);
  const last = data[data.length - 1];

  return (
    <div style={{ background: 'var(--bg-table)', border: '1px solid var(--border-light)', borderRadius: 12, padding: '14px 16px' }}>
      {title && (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 10 }}>
          <div style={{ fontSize: 12, color: 'var(--text-sub)', letterSpacing: '0.05em', textTransform: 'uppercase' }}>{title}</div>
          <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 14, fontWeight: 700, color: last.value >= 0 ? '#f04452' : '#3182f6' }}>
            {formatValue(last.value)}
          </div>
        </div>
      )}
      <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" style={{ width: '100%', height: H, overflow: 'visible' }}>
        {/* zero line */}
        {min < 0 && max > 0 && (
          <line x1="0" y1={zeroY} x2={W} y2={zeroY} stroke="var(--border-light)" strokeDasharray="3,3" />
        )}
        {/* area fill */}
        <path d={`${path} L ${W} ${H} L 0 ${H} Z`} fill={color} opacity="0.12" />
        {/* line */}
        <path d={path} fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        {/* points */}
        {data.map((d, i) => (
          <circle key={i} cx={toX(i)} cy={toY(d.value)} r="3" fill={color}>
            <title>{`${d.label}: ${formatValue(d.value)}`}</title>
          </circle>
        ))}
      </svg>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 9, color: 'var(--text-sub)', fontFamily: "'DM Mono', monospace", marginTop: 6 }}>
        <span>{data[0].label}</span>
        {data.length > 2 && <span>{data[Math.floor(data.length / 2)].label}</span>}
        <span>{last.label}</span>
      </div>
    </div>
  );
}

function EmptyBlock({ title, height, msg }) {
  return (
    <div style={{ background: 'var(--bg-table)', border: '1px solid var(--border-light)', borderRadius: 12, padding: '14px 16px' }}>
      {title && <div style={{ fontSize: 12, color: 'var(--text-sub)', marginBottom: 10, letterSpacing: '0.05em', textTransform: 'uppercase' }}>{title}</div>}
      <div style={{ height, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)', fontSize: 12 }}>{msg}</div>
    </div>
  );
}
